import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Card, Modal, Typography } from 'antd';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAddressKitInfo } from 'v2/hooks/useAddressKitInfo';
import type { AddressFormKit } from 'v2/interfaces/addressKit';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import kitService from 'v2/services/kit';

import AddressToDeliveryKit from './AddressToDeliveryKit';
import ConfirmAddress from './ConfirmSameAddress';
import ConfirmAddressToKits from './UpdateAddressForm';

const ModalBelt = () => {
  const { message } = App.useApp();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isOpenChangeAddress, setIsOpenChangeAddress] = React.useState(false);
  const [isOpenConfirmAddress, setIsOpenConfirmAddress] = React.useState(false);
  const queryClient = useQueryClient();

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [originalUser] = useObservable(() => eduzzAccountsService.getOriginalUser(), []);
  const isNotAccessControl = !!user && !!originalUser && user.id === originalUser.id;

  const { isLoading, isError, data, isFetching } = useAddressKitInfo(isNotAccessControl);

  const onSaveNewAddressToKit = (data: AddressFormKit) => {
    const formattedData = {
      ...data,
      number: data.number || null
    };

    return kitService.saveNewAddressToKit(formattedData);
  };

  const saveNewAddressToKit = useMutation({
    mutationFn: onSaveNewAddressToKit,
    onSuccess: () => queryClient.removeQueries({ queryKey: ['addressKitInfo'] })
  });

  React.useEffect(() => {
    const hasData = !!data && !data.answeredAt && !!user && !!originalUser;
    const isReady = !isLoading && !isError && !isFetching;
    setIsOpen(hasData && isReady && isNotAccessControl);
  }, [data, isLoading, isError, isFetching, user, originalUser, isNotAccessControl]);

  const handleChangeAddress = () => {
    setIsOpen(false);
    setIsOpenChangeAddress(true);
  };

  const handleConfirmAddress = async () => {
    try {
      await saveNewAddressToKit.mutateAsync(data.address);
      setIsOpen(false);
      setIsOpenConfirmAddress(true);
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Não foi possível confirmar o endereço.');
    }
  };

  const handleClose = () => {
    if (!saveNewAddressToKit.isPending) setIsOpen(false);
  };

  if (isLoading || isError || !data) return null;

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleClose}
        width={900}
        title='Parabéns pela nova conquista!'
        closable={false}
        footer={
          <div className='flex flex-wrap items-center justify-end gap-2'>
            {data.address && (
              <Button type='text' onClick={handleConfirmAddress} disabled={saveNewAddressToKit.isPending}>
                Meu endereço está correto!
              </Button>
            )}
            <Button type='primary' onClick={handleChangeAddress} disabled={saveNewAddressToKit.isPending}>
              {data.address ? 'Atualizar endereço de entrega' : 'Cadastrar endereço de entrega'}
            </Button>
          </div>
        }
      >
        <div className='flex flex-col gap-4'>
          <Typography.Text strong>Olá {data.name},</Typography.Text>
          <Typography.Paragraph className='mb-0'>
            Você alcançou o {data.belt}! Sua jornada no Programa Elite está evoluindo e queremos celebrar este marco com
            você.
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Confirme seu endereço</Typography.Text> para garantir a entrega do seu
            reconhecimento Elite.
          </Typography.Paragraph>

          <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            <Typography.Text strong className='mb-2 block'>
              Endereço atual:
            </Typography.Text>
            {!data.address ? (
              <Typography.Text type='secondary'>Nenhum endereço cadastrado.</Typography.Text>
            ) : (
              <Card size='small'>
                <AddressToDeliveryKit address={data.address} />
              </Card>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmAddressToKits isOpen={isOpenChangeAddress} setIsOpen={setIsOpenChangeAddress} data={data} />
      <ConfirmAddress isOpen={isOpenConfirmAddress} setIsOpen={setIsOpenConfirmAddress} data={data} />
    </>
  );
};

export default ModalBelt;
